import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTheme } from "./useTheme";
import { getToken } from "./tokenStorage";

const POLL_INTERVAL_MS = 2000;
const POLL_MAX_ATTEMPTS = 60;  // ~2 минуты

const STATUS_LABEL = {
  pending: "Ждём подтверждения оплаты…",
  waiting_for_capture: "Банк подтверждает платёж…",
  succeeded: "Оплата прошла",
  canceled: "Оплата отменена",
  refunded: "Средства возвращены",
};

export default function PaymentDone() {
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const [payment, setPayment] = useState(null);
  const [error, setError] = useState("");
  const [attempts, setAttempts] = useState(0);
  const timerRef = useRef(null);

  const paymentId = (() => {
    const q = new URLSearchParams(window.location.search);
    const fromQuery = q.get("pid") || q.get("payment_id");
    if (fromQuery && fromQuery !== "pending" && fromQuery !== "{PAYMENT_ID}") return fromQuery;
    try {
      return sessionStorage.getItem("pendingPaymentId");
    } catch {
      return null;
    }
  })();

  useEffect(() => {
    if (!paymentId) {
      setError("Не найден идентификатор платежа");
      return;
    }
    const token = getToken();
    if (!token) {
      setError("Нужно войти в аккаунт");
      return;
    }

    let cancelled = false;

    const poll = async () => {
      try {
        const r = await fetch(`/payments/${paymentId}/status`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!r.ok) {
          const err = await r.json().catch(() => ({}));
          throw new Error(err.detail || `HTTP ${r.status}`);
        }
        const data = await r.json();
        if (cancelled) return;
        setPayment(data);
        const terminal = ["succeeded", "canceled", "refunded"].includes(data.status);
        if (terminal) {
          try { sessionStorage.removeItem("pendingPaymentId"); } catch {}
          if (timerRef.current) {
            clearTimeout(timerRef.current);
            timerRef.current = null;
          }
          return;
        }
        setAttempts((a) => {
          const next = a + 1;
          if (next >= POLL_MAX_ATTEMPTS) {
            setError("Платёж ещё обрабатывается. Проверьте раздел «Инвентарь» позже.");
            return next;
          }
          timerRef.current = setTimeout(poll, POLL_INTERVAL_MS);
          return next;
        });
      } catch (e) {
        if (cancelled) return;
        setError(e.message || "Не удалось получить статус");
      }
    };

    poll();

    return () => {
      cancelled = true;
      if (timerRef.current) clearTimeout(timerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const bg = isDark ? "#0b0c12" : "#f5f6fa";
  const cardBg = isDark ? "#13141c" : "#ffffff";
  const borderColor = isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)";
  const textColor = isDark ? "#fff" : "#0f172a";
  const subTextColor = isDark ? "rgba(255,255,255,0.6)" : "rgba(0,0,0,0.55)";

  const status = payment?.status || (error ? "error" : "pending");
  const isSuccess = status === "succeeded";
  const isTerminal = ["succeeded", "canceled", "refunded"].includes(status) || !!error;

  const accent = isSuccess
    ? "#10b981"
    : status === "canceled" || status === "refunded"
      ? "#ef4444"
      : "#8b5cf6";

  return (
    <div style={{
      minHeight: "100vh",
      display: "flex", alignItems: "center", justifyContent: "center",
      padding: 24,
      background: bg,
      color: textColor,
    }}>
      <div style={{
        maxWidth: 520, width: "100%",
        background: cardBg,
        borderRadius: 20,
        border: `1px solid ${borderColor}`,
        padding: 28,
        boxShadow: "0 24px 60px rgba(0,0,0,0.3)",
      }}>
        <div style={{
          width: 72, height: 72,
          borderRadius: "50%",
          margin: "0 auto 18px",
          display: "flex", alignItems: "center", justifyContent: "center",
          background: `${accent}22`,
          border: `2px solid ${accent}`,
          fontSize: 32,
          animation: !isTerminal ? "payment-pulse 1.6s ease-in-out infinite" : "none",
        }}>
          {error ? "⚠️" : isSuccess ? "✓" : status === "canceled" || status === "refunded" ? "✕" : "⏳"}
        </div>

        <h1 style={{
          fontSize: 22, fontWeight: 800, textAlign: "center", margin: 0, marginBottom: 8,
        }}>
          {error
            ? "Что-то пошло не так"
            : isSuccess
              ? "Товар выдан!"
              : status === "canceled"
                ? "Оплата отменена"
                : status === "refunded"
                  ? "Средства возвращены"
                  : "Обрабатываем оплату…"}
        </h1>

        <p style={{
          color: subTextColor, textAlign: "center", margin: 0, marginBottom: 20,
          fontSize: 14, lineHeight: 1.5,
        }}>
          {error
            ? error
            : isSuccess
              ? "Открой инвентарь, чтобы экипировать покупку."
              : STATUS_LABEL[status] || "Ждём подтверждение…"}
        </p>

        {payment?.item && (
          <div style={{
            padding: 14,
            borderRadius: 14,
            background: isDark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.03)",
            border: `1px solid ${borderColor}`,
            display: "flex", alignItems: "center", gap: 12,
            marginBottom: 18,
          }}>
            {typeof payment.item.asset === "string" && payment.item.asset.startsWith("/") && (
              <img src={payment.item.asset} alt=""
                style={{ width: 56, height: 56, objectFit: "contain", borderRadius: 10 }} />
            )}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 14, fontWeight: 700 }}>{payment.item.name}</div>
              <div style={{ fontSize: 12, color: subTextColor }}>{payment.item.rarity}</div>
            </div>
            {payment.amount_rub != null && (
              <div style={{
                fontSize: 13, fontWeight: 700, whiteSpace: "nowrap",
              }}>
                {Math.floor(payment.amount_rub / 100)} ₽
              </div>
            )}
          </div>
        )}

        <div style={{ display: "flex", gap: 10 }}>
          {isSuccess ? (
            <button
              onClick={() => navigate("/account")}
              style={{
                flex: 1, height: 46, borderRadius: 12, border: "none",
                background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
                color: "#fff", fontSize: 15, fontWeight: 800, cursor: "pointer",
              }}
            >
              Перейти в инвентарь
            </button>
          ) : (
            <>
              <button
                onClick={() => navigate("/account")}
                style={{
                  flex: 1, height: 46, borderRadius: 12,
                  border: `1px solid ${borderColor}`,
                  background: "transparent",
                  color: textColor, fontSize: 14, fontWeight: 700, cursor: "pointer",
                }}
              >
                К аккаунту
              </button>
              {!isTerminal && (
                <button
                  disabled
                  style={{
                    flex: 1, height: 46, borderRadius: 12, border: "none",
                    background: `${accent}22`, color: accent,
                    fontSize: 14, fontWeight: 700,
                  }}
                >
                  Проверяем…
                </button>
              )}
            </>
          )}
        </div>
      </div>

      <style>{`
        @keyframes payment-pulse {
          0%,100% { transform: scale(1); box-shadow: 0 0 0 0 ${accent}44; }
          50%     { transform: scale(1.05); box-shadow: 0 0 0 14px ${accent}00; }
        }
      `}</style>
    </div>
  );
}
